import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import type { DeskLayoutApi } from "./useDeskLayout.ts";

interface DragState {
  id: string;
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  moved: boolean;
}

export interface CardDragApi {
  liftedId: string | null;
  dragging: boolean;
  zIndexOf: (id: string) => number | undefined;
  onPointerDown: (id: string, e: PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: PointerEvent<HTMLElement>) => void;
  onPointerEnd: (e: PointerEvent<HTMLElement>) => void;
  onClick: (id: string) => void;
  onKeyDown: (id: string, e: KeyboardEvent<HTMLElement>) => void;
}

/** A small wobble below this distance is still a tap, not a drag. */
const DRAG_THRESHOLD_PX = 4;
const KEY_STEP_PX = 20;
const KEY_STEP_LARGE_PX = 60;
const ARROW_STEPS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

interface ZOrder {
  bringToFront: (id: string) => void;
  zIndexOf: (id: string) => number | undefined;
}

function useZOrder(): ZOrder {
  const counter = useRef(10);
  const [zOrder, setZOrder] = useState<Record<string, number>>({});
  const bringToFront = useCallback((id: string) => {
    counter.current += 1;
    const z = counter.current;
    setZOrder((prev) => ({ ...prev, [id]: z }));
  }, []);
  const zIndexOf = useCallback((id: string) => zOrder[id], [zOrder]);
  return { bringToFront, zIndexOf };
}

/**
 * `opensOnMouseClick` marks cards that open on a plain mouse click (the search card);
 * the rest open on double-click so a click can start a drag. A drag never opens a card.
 * Taps open on the `click` that follows pointerup, not on pointerup itself, so that
 * trailing click can't land on (and close) the dialog that just opened.
 */
export function useCardDrag(
  desk: DeskLayoutApi,
  onOpen: (id: string) => void,
  opensOnMouseClick: (id: string) => boolean,
): CardDragApi {
  const drag = useRef<DragState | null>(null);
  const tappedId = useRef<string | null>(null);
  const [liftedId, setLiftedId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const { bringToFront, zIndexOf } = useZOrder();
  const { isStacked, getPlacement, moveCard, markMoved } = desk;

  const onPointerDown = useCallback(
    (id: string, e: PointerEvent<HTMLElement>) => {
      const placement = getPlacement(id);
      if (isStacked || e.button !== 0 || !placement) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      drag.current = { id, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, originX: placement.x, originY: placement.y, moved: false };
      setLiftedId(id);
      bringToFront(id);
    },
    [isStacked, getPlacement, bringToFront],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      if (!d.moved) setDragging(true);
      d.moved = true;
      moveCard(d.id, d.originX + dx, d.originY + dy);
    },
    [moveCard],
  );

  const onPointerEnd = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId) return;
      drag.current = null;
      setLiftedId(null);
      setDragging(false);
      const isTap = !d.moved && e.type === "pointerup";
      tappedId.current = isTap && (e.pointerType !== "mouse" || opensOnMouseClick(d.id)) ? d.id : null;
      if (d.moved) markMoved();
    },
    [markMoved, opensOnMouseClick],
  );

  const onClick = useCallback(
    (id: string) => {
      const opens = isStacked || tappedId.current === id;
      tappedId.current = null;
      if (opens) onOpen(id);
    },
    [isStacked, onOpen],
  );

  const onKeyDown = useCardKeys(desk, onOpen, bringToFront);
  return { liftedId, dragging, zIndexOf, onPointerDown, onPointerMove, onPointerEnd, onClick, onKeyDown };
}

/** Enter or Space opens a card; arrow keys nudge it (Shift for bigger steps). */
function useCardKeys(
  desk: DeskLayoutApi,
  onOpen: (id: string) => void,
  bringToFront: (id: string) => void,
): (id: string, e: KeyboardEvent<HTMLElement>) => void {
  const { isStacked, getPlacement, moveCard, markMoved } = desk;
  return useCallback(
    (id: string, e: KeyboardEvent<HTMLElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen(id);
        return;
      }
      const step = ARROW_STEPS[e.key];
      const placement = getPlacement(id);
      if (!step || !placement || isStacked) return;
      e.preventDefault();
      const size = e.shiftKey ? KEY_STEP_LARGE_PX : KEY_STEP_PX;
      bringToFront(id);
      moveCard(id, placement.x + step[0] * size, placement.y + step[1] * size);
      markMoved();
    },
    [isStacked, getPlacement, moveCard, markMoved, onOpen, bringToFront],
  );
}
